import { Trans, useLingui } from '@lingui/react/macro';
import {
  BrandDesignSystemProvider,
  BrandFontLoader,
  BrandGuidelinesProvider,
  getTemplate,
} from '@magig/creative/react';
import {
  type BrandDesignSystem,
  type BrandGuidelines,
  type CreativeAsset,
  DEFAULT_BRAND_DESIGN_SYSTEM,
  DEFAULT_BRAND_GUIDELINES,
} from '@magig/schemas';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { env } from '../lib/env.js';

// /render/creative/$id — chromeless render surface (M1 s157).
//
// Mounted by:
//   - founders previewing a draft creative in the dashboard
//   - the playwright worker (s158) when it screenshots the canvas to
//     ship the result to R2 as a PNG/jpg
//
// Everything renders inside a fixed-position overlay sized to the
// creative's format dimensions and pinned to the top-left so the
// worker's viewport screenshot lines up exactly with the template
// canvas. RouterProgress + RefetchIndicator from __root sit behind
// us via z-index so they never leak into the capture.
//
// No auth gate today — the UUID is the only secret. Signed-token
// access lands when the BullMQ render worker comes online.

interface RenderEnvelope {
  creativeAsset: CreativeAsset;
  guidelines: BrandGuidelines | null;
  /** §3.1c Phase 3 (s238) — brand design system, null when the
   *  brain hasn't been populated yet. Falls back to
   *  DEFAULT_BRAND_DESIGN_SYSTEM via the provider. */
  designSystem: BrandDesignSystem | null;
}

async function fetchRenderEnvelope(id: string): Promise<RenderEnvelope> {
  const res = await fetch(`${env.API_URL}/api/creative-assets/${encodeURIComponent(id)}/render`);
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/creative-assets/${id}/render ${res.status} ${detail}`);
  }
  return (await res.json()) as RenderEnvelope;
}

export const Route = createFileRoute('/render/creative/$id')({
  component: RenderPage,
});

function RenderPage() {
  const { t } = useLingui();

  const { id } = Route.useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['render-creative', id] as const,
    queryFn: () => fetchRenderEnvelope(id),
    staleTime: 60_000,
    // Disable refetch-on-focus — the worker captures a single frame.
    refetchOnWindowFocus: false,
  });

  if (isLoading)
    return (
      <RenderShell>
        <LoadingPip />
      </RenderShell>
    );
  if (error)
    return (
      <RenderShell>
        <RenderError message={error instanceof Error ? error.message : String(error)} />
      </RenderShell>
    );
  if (!data)
    return (
      <RenderShell>
        <RenderError message={t`no data`} />
      </RenderShell>
    );

  const { creativeAsset, guidelines, designSystem } = data;

  const template = creativeAsset.templateKey ? getTemplate(creativeAsset.templateKey) : null;

  if (!template) {
    return (
      <RenderShell>
        <RenderError
          message={`no template registered for key "${creativeAsset.templateKey ?? '∅'}" — generator was ${creativeAsset.generator}`}
        />
      </RenderShell>
    );
  }

  // Validate the saved fill against the template's own schema. Strict
  // — a parse failure means Mag wrote malformed JSON or the template
  // moved on without a migration. Either case is a hard error; we
  // surface it instead of rendering something broken.
  const parsed = template.schema.safeParse(creativeAsset.templateFill ?? template.defaults);
  if (!parsed.success) {
    return (
      <RenderShell>
        <RenderError
          message={`fill schema mismatch: ${parsed.error.issues.map((i) => i.message).join('; ')}`}
        />
      </RenderShell>
    );
  }

  const fill = parsed.data;
  const TemplateComponent = template.Component;
  return (
    <RenderShell>
      {/* s241 — dynamically load the brand's fonts (Plus Jakarta
          Sans, Instrument Serif, etc.). Without this, every render
          falls through to system-ui regardless of what the vision
          pass captured. Playwright's wait-for-fonts-ready step
          picks them up before the screenshot. */}
      <BrandFontLoader guidelines={guidelines} />
      <BrandGuidelinesProvider guidelines={guidelines ?? DEFAULT_BRAND_GUIDELINES}>
        <BrandDesignSystemProvider designSystem={designSystem ?? DEFAULT_BRAND_DESIGN_SYSTEM}>
          <TemplateComponent fill={fill} format={creativeAsset.format} />
        </BrandDesignSystemProvider>
      </BrandGuidelinesProvider>
    </RenderShell>
  );
}

/** Fixed-position overlay that pins the template canvas to (0, 0) so
 *  the playwright worker's screenshot lines up to the pixel. White
 *  page background catches any transparent template area. */
function RenderShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#ffffff',
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', left: 0, top: 0 }}>{children}</div>
    </div>
  );
}

function LoadingPip() {
  return (
    <div
      style={{
        width: 540,
        height: 540,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F4EFE6',
        color: '#0F0E17',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        fontSize: 18,
        opacity: 0.5,
      }}
    >
      <Trans>mag is preparing the canvas…</Trans>
    </div>
  );
}

function RenderError({ message }: { message: string }) {
  return (
    <div
      style={{
        width: 540,
        height: 540,
        padding: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FFE0E0',
        color: '#0F0E17',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        fontSize: 14,
        textAlign: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Trans>↘ render failed · {message}</Trans>
    </div>
  );
}
